/**
 * -----------------------------------------------------------------------------
 * DISCLAIMER
 * -----------------------------------------------------------------------------
 * This file is part of the Breeze client application.
 * The functions, types, and controls defined here are intended for internal use
 * within the project and may not cover every edge case. They are provided
 * "AS IS" without warranty of any kind, express or implied, including but not
 * limited to correctness, completeness, or fitness for a particular purpose.
 *
 * By using these functions you accept that:
 * - They may change or be removed without notice.
 * - You are responsible for verifying correctness for your specific use case.
 * - The maintainers are not liable for any damage or issues resulting from use.
 *
 * When reusing or extending these functions:
 * - Ensure inputs are validated.
 * - Wrap in appropriate error handling.
 * - Keep performance considerations in mind (especially on large datasets).
 * -----------------------------------------------------------------------------
 */
// SearchControl.ts
import * as L from "leaflet";
import { SEARCH_ICONS } from "./icons";
import { setQueryValue } from "../utils/urlUtils";
import { barbiefyToggle } from "../../map/styles/barbiefy";
import { DataSampleType, DataSampleUnit, StationStatus } from "../types";
import { getStatusManager } from "../../map/state/statusLayers";
import { closeSidebar } from "./sidebar";

export type StationEntry = {
	id: string;
	marker: L.Marker;
	name: string;
	owner: string;
	content?: string;
};

export type PortEntry = {
	id: string;
	name: string;
	ne: { lat: number, lng: number }
	sw: { lat: number, lng: number }
};
export type ApiPortEntry = {
	Id: string;
	Name: string;
	NorthEast: { Latitude: number, Longitude: number }
	SouthWest: { Latitude: number, Longitude: number }
};
export type SampleGroup = {
	Title: string;
	Elements: GroupElement[],
};
export type GroupElement = {
	Title: string;
	Element: DataSampleType,
};

export type HistPanelConfiguration = {
	WindRoseIntervalHours: number | null,
	ChartRangeHours: number[] | null,
	DefaultRangeHours: number | null
}
export type ApiViewEntry = {
	Id: string;
	Name: string;
	DefaultStationIdToSelect: string,
	HistPanelConfiguration: HistPanelConfiguration,
	NowPanelGrouping: SampleGroup[] | null,
	NorthEast: { Latitude: number, Longitude: number }
	SouthWest: { Latitude: number, Longitude: number }
	SampleTypeUnitMapping: Record<DataSampleType, DataSampleUnit> | null,
};

type Result =
	| { type: "station"; id: string; label: string; marker: L.Marker }
	| { type: "port"; id: string; label: string; portEntry: PortEntry };

type HiddenLayerStationFoundArgs = {
	pin: StationEntry;
	stationStatus: StationStatus;
}
export interface SearchControlOptions extends L.ControlOptions {
	placeholder?: string;
	minChars?: number;       // start searching after this many chars
	maxResults?: number;     // limit dropdown size
	stations?: StationEntry[];
	ports?: PortEntry[];
	// Optional: if you use MarkerCluster, provide the group so we can zoomToShowLayer
	markerClusterGroup?: L.MarkerClusterGroup;
	// Optional hooks
	onSelectStation?: (station: StationEntry) => void;
	onSelectPort?: (port: PortEntry) => void;
	// Optional: custom filter (return [score, include?]) — higher score first
	ranker?: (query: string, label: string, body?: string) => number | null;
	// Optional: debounce (ms)
	debounceMs?: number;
	onHiddenLayerStationFound?: (args: HiddenLayerStationFoundArgs) => void;
}

export class SearchControl extends L.Control {
	private containerEl!: HTMLElement;
	private inputEl!: HTMLInputElement;
	private listEl!: HTMLUListElement;

	private stations: StationEntry[] = [];
	private ports: PortEntry[] = [];
	private opts: Required<Omit<SearchControlOptions,
		"stations" | "ports" | "markerClusterGroup" | "onSelectStation" | "onSelectPort" | "ranker">> &
		Pick<SearchControlOptions, "stations" | "ports" | "markerClusterGroup" | "onSelectStation" | "onSelectPort" | "ranker">;

	private results: Result[] = [];
	private highlightedIndex = -1;
	private searchTimer: number | undefined;

	constructor(options?: SearchControlOptions) {
		super(options);
		this.opts = {
			position: options?.position ?? "topright",
			placeholder: options?.placeholder ?? "Search...",
			minChars: options?.minChars ?? 2,
			maxResults: options?.maxResults ?? 8,
			debounceMs: options?.debounceMs ?? 120,
			stations: options?.stations ?? [],
			ports: options?.ports ?? [],
			markerClusterGroup: options?.markerClusterGroup,
			onSelectStation: options?.onSelectStation,
			onSelectPort: options?.onSelectPort,
			ranker: options?.ranker,
			onHiddenLayerStationFound: options?.onHiddenLayerStationFound
		};
		this.stations = this.opts.stations ?? [];
		this.ports = this.opts.ports ?? [];
	}

	onAdd(map: L.Map) {
		this.containerEl = L.DomUtil.create("div", "leaflet-searchbox");
		this.inputEl = L.DomUtil.create("input", "leaflet-searchbox-input", this.containerEl) as HTMLInputElement;
		this.inputEl.id = "searchbox";
		this.inputEl.type = "text";
		this.inputEl.placeholder = this.opts.placeholder;

		this.listEl = L.DomUtil.create("ul", "leaflet-searchbox-list", this.containerEl) as HTMLUListElement;

		// Prevent map interactions when using the search UI
		L.DomEvent.disableClickPropagation(this.containerEl);
		L.DomEvent.disableScrollPropagation(this.containerEl);

		// Events
		this.inputEl.addEventListener("input", () => this.queueSearch());
		this.inputEl.addEventListener("keydown", (e) => this.handleKeydown(e));
		document.addEventListener("pointerdown", this.handleOutsideClick);

		return this.containerEl;
	}

	onRemove() {
		document.removeEventListener("pointerdown", this.handleOutsideClick);
	}
	/** Public: update indices dynamically */
	setStations(stations: StationEntry[]) {
		this.stations = stations ?? [];
		this.queueSearch(true);
	}
	setPorts(ports: PortEntry[]) {
		this.ports = ports ?? [];
		this.queueSearch(true);
	}
	

	private handleOutsideClick = (e: PointerEvent) => {
		if (!this.containerEl.contains(e.target as Node)) {
			this.clearResults();
		}
	};

	private queueSearch(force = false) {
		if (this.searchTimer) window.clearTimeout(this.searchTimer);
		const delay = force ? 0 : this.opts.debounceMs;
		this.searchTimer = window.setTimeout(() => this.doSearch(), delay) as unknown as number;
	}

	private doSearch() {
		const q = this.inputEl.value.trim();
		if (q.length < this.opts.minChars) {
			this.clearResults();
			return;
		}

		const normalized = q.toLowerCase();
		const ranker = this.opts.ranker ?? defaultRanker;

		if (normalized === "barbie") {
			barbiefyToggle();
		}

		const stationResults: Array<{ score: number; value: Result }> = [];
		for (const p of this.stations) {
			let label = p.name ?? "";
			const owner = p.owner ?? "";
			if (owner !== "") {
				label += " (" + owner + ")";
			}
			const body = p.content ?? "";
			const s = ranker(normalized, label, body);
			if (s !== null) {
				stationResults.push({ score: s, value: { type: "station", id: p.id, label, marker: p.marker } });
			}
		}

		const portResults: Array<{ score: number; value: Result }> = [];
		for (const port of this.ports) {
			const label = port.name ?? "";
			const s = ranker(normalized, label, undefined);
			if (s !== null) {
				portResults.push({ score: s, value: { type: "port", id: port.id, label, portEntry: port } });
			}
		}

		const combined = [...stationResults, ...portResults]
			.sort((a, b) => b.score - a.score)
			.slice(0, this.opts.maxResults)
			.map(x => x.value);

		this.results = combined;
		this.highlightedIndex = combined.length ? 0 : -1;
		this.renderList();
	}

	private renderList() {
		this.listEl.innerHTML = "";
		if (!this.results.length) {
			this.listEl.style.display = "none";
			return;
		}
		this.listEl.style.display = "block";

		for (let i = 0; i < this.results.length; i++) {
			const r = this.results[i];
			const li = document.createElement("li");
			li.className = "leaflet-searchbox-item" + (i === this.highlightedIndex ? " is-active" : "");
			li.dataset.index = String(i);
			li.innerHTML = `<i class="${SEARCH_ICONS[r.type]}" style="font-size: 20px"></i>${r.label}`;
			li.addEventListener("mouseenter", () => {
				this.highlightedIndex = i;
				this.renderList();
			});
			li.addEventListener("pointerdown", (e) => {
				e.preventDefault();
				e.stopPropagation();
				this.selectIndex(i);
			});
			this.listEl.appendChild(li);
		}
	}

	private clearResults() {
		this.results = [];
		this.highlightedIndex = -1;
		this.listEl.innerHTML = "";
		this.listEl.style.display = "none";
	}

	private handleKeydown(e: KeyboardEvent) {
		if (!this.results.length) return;

		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				this.highlightedIndex = (this.highlightedIndex + 1) % this.results.length;
				this.renderList();
				break;
			case "ArrowUp":
				e.preventDefault();
				this.highlightedIndex =
					(this.highlightedIndex - 1 + this.results.length) % this.results.length;
				this.renderList();
				break;
			case "Enter":
				e.preventDefault();
				if (this.highlightedIndex >= 0) this.selectIndex(this.highlightedIndex);
				break;
			case "Escape":
				this.clearResults();
				break;
		}
	}

	public selectPort(id: string) {
		const area = this.ports.find(a => a.id.toLowerCase() === id.toLowerCase());
		if (area) {
			closeSidebar();
			const map = (this as any)._map as L.Map | undefined;
			const bounds = L.latLngBounds(area.sw, area.ne);
			map?.fitBounds(bounds, { animate: false });
			this.opts.onSelectPort?.(area);
		}
	}
	public selectView(view: ApiViewEntry | undefined) {
		if (view) {
			closeSidebar();
			const area = {
				sw: {
					lng: view.SouthWest.Longitude,
					lat: view.SouthWest.Latitude
				},
				ne: {
					lng: view.NorthEast.Longitude,
					lat: view.NorthEast.Latitude
				}
			};
			const map = (this as any)._map as L.Map | undefined;
			const bounds = L.latLngBounds(area.sw, area.ne);
			map?.fitBounds(bounds, { animate: false });
		}
	}

	public selectStation(id: string, fromQuery = false) {
		try {
			const pin = this.stations.find(p =>
				p.id.toLowerCase() === id.toLowerCase() ||
				p.content?.toLowerCase() === id.toLowerCase()
			);

			if (!pin) {
				return;
			}

			const map = (this as any)._map as L.Map | undefined;
			if (!map) {
				return;
			}

			const latLng = pin.marker.getLatLng();
			const clusterGroup = this.opts.markerClusterGroup as any;

			const isVisibleInCluster =
				clusterGroup &&
				typeof clusterGroup.hasLayer === "function" &&
				clusterGroup.hasLayer(pin.marker);

			const openStation = () => {
				this.opts.onSelectStation?.(pin);

				if (map.hasLayer(pin.marker)) {
					pin.marker.fireEvent("click");
				}
			};

			if (isVisibleInCluster && typeof clusterGroup.zoomToShowLayer === "function") {
				clusterGroup.zoomToShowLayer(pin.marker, () => {
					map.flyTo(latLng, 10, { animate: !fromQuery });
					openStation();
				});
				return;
			}

			map.flyTo(latLng, 10, { animate: !fromQuery });

			this.opts.onSelectStation?.(pin);
			const layerKey = getStatusManager().getStatus(pin.content);
			const states = getStatusManager().getState();
			
			if (layerKey && !states.enabled[layerKey]) {
				this.opts.onHiddenLayerStationFound?.({
					pin,
					stationStatus: layerKey
				});
			} else {
				pin.marker.fireEvent("click");
			}
		} catch (error) {
			console.error("Error loading jumping to station", error);
		}
	}

	private selectIndex(i: number) {
		const r = this.results[i];
		if (!r) return;
		this.inputEl.value = "";
		this.clearResults();
		if (r.type === "station") {
			this.selectStation(r.id);
		} else {
			//setQueryValue({ port: r.id });
			this.selectPort(r.id);
		}
	}
}

let debugRect: L.Rectangle | null = null;
export function showSavedBBox(map: L.Map, view: { sw: L.LatLngExpression; ne: L.LatLngExpression }) {
	const b = L.latLngBounds(view.sw, view.ne);
	if (debugRect) debugRect.remove();
	debugRect = L.rectangle(b, { weight: 4, fillOpacity: 0, dashArray: '8', color: "red" }).addTo(map);
}

function defaultRanker(q: string, label: string, body?: string): number | null {
	const l = (label ?? "").toLowerCase();
	const b = (body ?? "").toLowerCase();

	// Score: exact start > anywhere > body hit
	if (l.startsWith(q)) return 100 - (l.length - q.length);      // prefer shorter exact starts
	if (l.includes(q)) return 60 - (l.indexOf(q) ?? 0);
	if (b && b.includes(q)) return 20 - (b.indexOf(q) ?? 0);

	// Allow multiple tokens "harb nor" to match "Harbor North"
	const tokens = q.split(/\s+/).filter(Boolean);
	if (tokens.length > 1 && tokens.every(t => l.includes(t) || b.includes(t))) {
		return 40 - tokens.reduce((acc, t) => acc + (l.indexOf(t) >= 0 ? l.indexOf(t) : b.indexOf(t)), 0);
	}

	return null;
}

type LatLng = { lat: number; lng: number };

function buildFeatureBounds(stations: LatLng[], fairway?: LatLng[] | null): L.LatLngBounds {
	let b: L.LatLngBounds | null = null;
	const add = (p: LatLng) => {
		const ll = L.latLng(p.lat, p.lng);
		b = b ? b.extend(ll) : L.latLngBounds(ll, ll);
	};

	stations.forEach(add);
	(fairway ?? []).forEach(add);
	if (!b) b = L.latLngBounds([0, 0], [0.0001, 0.0001]);
	return b;
}

export function captureFeatureView(stations: LatLng[], fairway?: LatLng[] | null): Partial<PortEntry> {
	const b = buildFeatureBounds(stations, fairway);
	return {
		sw: { lat: b.getSouth(), lng: b.getWest() },
		ne: { lat: b.getNorth(), lng: b.getEast() }
	};
}
